import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import signatureStyles from '../../formParts/signatureSentinel/styles.module.scss'
import responsiveSignatureStyles from '../../formParts/signatureSentinel/responsiveStyles.module.scss'

import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import SignatureCanvas from 'react-signature-canvas'
import axios from 'axios'
import { SubmitButton } from '..'
import { useParams } from 'react-router-dom'
import { trackSubmitApplication } from '../../../helpers/handleTracking'
import { useClaimData, useUpdateClaimData } from '../../../claimProvider'
import { useViewThruDevice, getViewThruVerifyBaseUrl, ViewThruOtpModal } from '../../../viewThru'
import type { ViewThruVerifyResponse, ViewThruCheckOtpResponse } from '../../../viewThru'
import Previewer from '../previewer'

interface sigProps {
    additionalAction?: () => void | null
    extraPayload?: any
    children?: React.ReactNode
    stepData?: any
    requiredFields?: string[]
    /** When true, the submit button stays disabled (e.g. external gating). */
    submitDisabled?: boolean
}

const OTP_FALLBACK_ERROR = 'We could not check that code. Please try again in a moment.'

type OtpModalState = {
    verificationId: string
    sessionId: string
    origin: string
    errorMessage: string | null
    isSubmitting: boolean
}

export const SignaturePad = ({
    extraPayload,
    additionalAction,
    children,
    stepData,
    requiredFields = [],
    submitDisabled = false,
}: sigProps) => {
    const signCanvas = React.useRef<SignatureCanvas>(null) as React.MutableRefObject<any>
    const [sigData, setSigData] = useState('')
    const [allRequiredChecked, setAllRequiredChecked] = useState(requiredFields.length === 0)
    const [otpModal, setOtpModal] = useState<OtpModalState | null>(null)
    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()
    const { token: routeToken } = useParams()
    const token = routeToken ?? claimData?.sigData?.token
    const { deviceSessionId, originToken, isReady, refreshVerificationData } = useViewThruDevice()
    const viewThruRequired = process.env.REACT_APP_VIEWTHRU_ENABLED === 'true'
    const viewThruSimulateOtp =
        viewThruRequired && (process.env.REACT_APP_VIEWTHRU_SIMULATE_OTP ?? '').toLowerCase() === 'true'
    const viewThruReady = !viewThruRequired || isReady
    const canSubmit = allRequiredChecked && viewThruReady && !submitDisabled

    useEffect(() => {
        if (stepData) {
            console.log('StepData updated:', stepData)
            setAllRequiredChecked(requiredFields.every((field) => stepData[field] === 'yes'))
        }
    }, [stepData, requiredFields])

    useEffect(() => {
        console.log('Checkbox states:', { requiredFields, allRequiredChecked })
    }, [requiredFields, allRequiredChecked])

    const saveSign = () => {
        signCanvas.current.on()
        let x = signCanvas.current.toDataURL('image/png')
        setSigData(x)
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        clearSigPad()
    }

    const clearSigPad = () => {
        signCanvas.current.clear()
        signCanvas.current.on()
        setSigData('')
    }

    const getSignEndpoint = (payloadType: unknown) => {
        let signEndpoint = process.env.REACT_APP_API_SIGN_URL
        if (payloadType === 'sentinelSignIL') signEndpoint = process.env.REACT_APP_API_SIGN_IL_URL
        if (payloadType === 'webform_IRRESPONSIBLE_BOSH') signEndpoint = process.env.REACT_APP_API_SIGN_IL_URL
        return signEndpoint
    }

    const resolveViewThruVerificationId = async (
        sessionId: string,
        origin: string,
        leadOrigin: string
    ): Promise<string | null> => {
        const verifyBaseUrl = getViewThruVerifyBaseUrl()
        if (!verifyBaseUrl) {
            Report.failure(
                'Configuration Error',
                'ViewThru verification is not configured. Please contact support@sentinellegal.co.uk',
                'OK'
            )
            return null
        }

        Loading.circle('Verifying identity')
        try {
            const verifyResponse = await axios.post<ViewThruVerifyResponse>(`${verifyBaseUrl}/viewthru/verify`, {
                leadOrigin,
                token,
                kountDeviceSessionId: sessionId,
                viewThruOriginToken: origin,
            })

            const verifyData = verifyResponse.data

            if (!verifyData.success) {
                Loading.remove()
                Report.failure(
                    'Verification Failed',
                    verifyData.message ??
                        'We could not verify your identity. Please try again or contact support@sentinellegal.co.uk',
                    'OK'
                )
                return null
            }

            const showOtpModal =
                Boolean(verifyData.verification_id) &&
                (viewThruSimulateOtp || verifyData.decision === 'CHALLENGE')

            if (showOtpModal && verifyData.verification_id) {
                Loading.remove()
                setOtpModal({
                    verificationId: verifyData.verification_id,
                    sessionId: sessionId ?? '',
                    origin: origin ?? '',
                    errorMessage: null,
                    isSubmitting: false,
                })
                return null
            }

            if (verifyData.decision === 'ALLOW' && verifyData.verification_id) {
                return verifyData.verification_id
            }

            Loading.remove()
            Report.failure(
                'Verification Failed',
                verifyData.message ?? 'Unexpected verification response. Please try again.',
                'OK'
            )
            return null
        } catch (error: any) {
            Loading.remove()
            const message =
                error?.response?.data?.message ??
                'We could not verify your identity. Please try again or contact support@sentinellegal.co.uk'
            Report.failure('Verification Failed', message, 'OK')
            return null
        }
    }

    const submitSignRequest = async (
        sessionId: string | undefined,
        origin: string | undefined,
        viewThruVerificationId: string | undefined
    ) => {
        Loading.circle('Signing Documents')

        let payload: Record<string, unknown> = {
            token,
            type: '',
            sigData,
        }

        if (extraPayload && Object.keys(extraPayload).length) {
            payload = { ...payload, ...extraPayload }
        }

        if (sessionId) {
            payload = { ...payload, kountDeviceSessionId: sessionId }
        }
        if (origin) {
            payload = { ...payload, viewThruOriginToken: origin }
        }
        if (viewThruVerificationId) {
            payload = { ...payload, viewThruVerificationId }
        }

        console.log('[ViewThru] sign payload tokens:', {
            kountDeviceSessionId: sessionId,
            viewThruOriginTokenPresent: Boolean(origin),
            viewThruVerificationIdPresent: Boolean(viewThruVerificationId),
            type: payload.type,
        })

        const signEndpoint = getSignEndpoint(payload.type)

        try {
            const response = await axios.post(`${signEndpoint}`, payload)
            console.log('[SentinelMatters] sign response:', response.status, response.data)
            Loading.remove()

            if (response.data !== 'SUCCESS') {
                if (!response?.data?.success) {
                    const serverMessage =
                        typeof response?.data?.message === 'string' ? response.data.message : null
                    Report.failure(
                        'An Error Occurred',
                        serverMessage ??
                            'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                        'Okay'
                    )
                    return
                }

                updateClaim({
                    ...claimData,
                    openBankingUrl: response.data.shortLink,
                })
                return
            }

            if (additionalAction) additionalAction()
            trackSubmitApplication()
        } catch (error: any) {
            console.error('[SentinelMatters] sign error:', error?.response?.status, error?.response?.data ?? error)
            Loading.remove()
            const serverMessage =
                typeof error?.response?.data?.message === 'string' ? error.response.data.message : null
            Report.failure(
                'An Error Occurred',
                serverMessage ??
                    'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                'Okay'
            )
        }
    }

    const submitSignature = async () => {
        if (sigData.length < 1) {
            Report.failure('Please Sign', 'Please enter your signature onto the signature pad to continue.', 'OK')
            return
        }

        if (!allRequiredChecked) {
            Report.failure(
                'Please Confirm All Declarations',
                'Please confirm all the required declarations to continue.',
                'OK',
            )
            return
        }

        if (submitDisabled) {
            Report.failure(
                'Please Wait',
                'We are still preparing your session. Please try again in a moment.',
                'OK',
            )
            return
        }

        let sessionId = deviceSessionId
        let origin = originToken

        if (viewThruRequired) {
            const fresh = await refreshVerificationData()
            sessionId = fresh?.deviceSessionId ?? sessionId
            origin = fresh?.originToken ?? origin
            if (!sessionId || !origin) {
                console.warn('[ViewThru] sign blocked — missing device session or origin token after refresh')
                Report.failure(
                    'Please Wait',
                    'We are still preparing your session. Please try again in a moment.',
                    'OK',
                )
                return
            }

            const verificationId = await resolveViewThruVerificationId(
                sessionId,
                origin,
                (extraPayload?.type as string) ?? 'webform_SENTINEL_MATTERS'
            )
            if (!verificationId) return

            await submitSignRequest(sessionId, origin, verificationId)
            return
        }

        await submitSignRequest(sessionId ?? undefined, origin ?? undefined, undefined)
    }

    const handleOtpSubmit = async (otpCode: string) => {
        if (!otpModal) return

        const verifyBaseUrl = getViewThruVerifyBaseUrl()
        setOtpModal((current) => (current ? { ...current, isSubmitting: true, errorMessage: null } : current))

        try {
            const otpResponse = await axios.post<ViewThruCheckOtpResponse>(`${verifyBaseUrl}/viewthru/check-otp`, {
                verification_id: otpModal.verificationId,
                otp_code: otpCode,
            })

            if (!otpResponse.data.success || !otpResponse.data.verification_id) {
                setOtpModal((current) =>
                    current
                        ? {
                              ...current,
                              isSubmitting: false,
                              errorMessage: otpResponse.data.message ?? OTP_FALLBACK_ERROR,
                          }
                        : current
                )
                return
            }

            const { sessionId, origin } = otpModal
            const verificationId = otpResponse.data.verification_id
            setOtpModal(null)
            await submitSignRequest(sessionId, origin, verificationId)
        } catch (error: any) {
            const message = error?.response?.data?.message ?? OTP_FALLBACK_ERROR
            setOtpModal((current) =>
                current ? { ...current, isSubmitting: false, errorMessage: message } : current
            )
        }
    }

    const handleOtpCancel = () => {
        setOtpModal(null)
    }

    return (
        <div className={styles.signaturePadContent}>
            {otpModal ? (
                <ViewThruOtpModal
                    onSubmit={handleOtpSubmit}
                    onCancel={handleOtpCancel}
                    errorMessage={otpModal.errorMessage}
                    isSubmitting={otpModal.isSubmitting}
                />
            ) : null}
            <div className={`${styles.sigPadContainer}  `}>
                <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                <div>
                    <SignatureCanvas
                        ref={signCanvas}
                        onEnd={saveSign}
                        clearOnResize={false}
                        penColor="grey"
                        canvasProps={{
                            width: window.innerWidth < 760 ? 350 : 760,
                            height: 300,
                            className: 'sigCanvas',
                        }}
                    />
                    <span onClick={clearSign}>Clear</span>
                </div>
            </div>
            <div>{children}</div>
            <div className={styles.buttonContainer}>
                <SubmitButton
                    text="Submit"
                    btnType="primary"
                    onClick={submitSignature}
                    disabled={!canSubmit || Boolean(otpModal)}
                />
            </div>
            <div className={styles.compensationText}>
                <p className={styles.compensationLine1}>You could be owed compensation</p>
                <p className={styles.compensationLine2}>with a per client average value of</p>
                <p className={styles.amount}>£5,318*</p>
            </div>

            <div className={`${signatureStyles.documentsSection} ${responsiveSignatureStyles.documentsSection}`}>
                <h3 className={`${signatureStyles.documentsTitle} ${responsiveSignatureStyles.documentsTitle}`}>
                    Your Retainer & Client Care Documents
                </h3>
                <p className={`${signatureStyles.documentsSubtitle} ${responsiveSignatureStyles.documentsSubtitle}`}>
                    Please carefully review these documents before providing your signature. These documents form your
                    agreement with Sentinel Legal.
                </p>

                {claimData && (
                    <div
                        className={`${signatureStyles.documentsContainer} ${responsiveSignatureStyles.documentsContainer}`}
                    >
                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Conditional Fee Agreement (CFA)</h4>
                            <p>
                                This agreement sets out the terms of our no-win, no-fee arrangement and explains how our
                                fees are calculated.
                            </p>
                            {claimData?.cfaUrl ? (
                                <Previewer url={claimData.cfaUrl} documentString={'View CFA Document'} />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>CFA document not yet available</p>
                            )}
                        </div>

                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Letter of Authority (LOA)</h4>
                            <p>
                                This document authorizes Sentinel Legal to act on your behalf and communicate with your
                                finance provider.
                            </p>
                            {claimData?.loaUrl ? (
                                <Previewer url={claimData.loaUrl} documentString={'View LOA Document'} />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>LOA document not yet available</p>
                            )}
                        </div>

                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Assignment of Funds</h4>
                            <p>
                                This document assigns your compensation funds to Sentinel Legal to cover legal fees and
                                disbursements in accordance with your Conditional Fee Agreement.
                            </p>
                            {claimData?.assignmentOfFundsUrl ? (
                                <Previewer
                                    url={claimData.assignmentOfFundsUrl}
                                    documentString={'View Assignment of Funds Document'}
                                />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>
                                    Assignment of Funds document not yet available
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
